<template>
  <a-modal
    :title="title"
    :width="1200"
    :visible="visible"
    :confirmLoading="confirmLoading"
    @ok="handleSubmit"
    @cancel="handleCancel"
  >
    <a-spin :spinning="confirmLoading">
      <a-form-model ref="form" :model="form" :rules="rules" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }">
        <a-form-model-item label="退货供应商" prop="supplierId">
          <a-select
            v-model="form.supplierId"
            placeholder="请选择退货供应商（必填）"
            style="width: 300px"
            @change="handleSupplierChange"
          >
            <a-select-option v-for="item in supplierOptions" :key="item.id" :value="item.id">
              {{ item.supplierName }}
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item label="退货原因" prop="returnReason">
          <a-select
            v-model="form.returnReason"
            placeholder="请选择退货原因（必填）"
            style="width: 300px"
          >
            <a-select-option :value="1">质量问题</a-select-option>
            <a-select-option :value="2">错发漏发</a-select-option>
            <a-select-option :value="3">滞销退货</a-select-option>
            <a-select-option :value="4">其他</a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item label="备注" prop="remark">
          <a-textarea
            v-model="form.remark"
            placeholder="请输入备注信息（选填）"
            :rows="2"
            :maxLength="200"
            style="width: 600px"
          />
        </a-form-model-item>
      </a-form-model>

      <!-- 商品退货数量设置区域 -->
      <div>
        <a-divider>商品退货数量设置</a-divider>

        <!-- 选择退货商品 -->
        <a-form layout="inline" style="margin-bottom: 16px; background: #f5f5f5; padding: 12px; border-radius: 4px;">
          <a-form-item label="选择商品">
            <a-select
              v-model="selectedGoodsIds"
              mode="multiple"
              placeholder="请选择退货商品（支持搜索）"
              show-search
              :filter-option="false"
              :default-active-first-option="false"
              :not-found-content="notFoundContent"
              @search="handleSearch"
              @change="handleGoodsChange"
              @dropdownVisibleChange="handleDropdownVisibleChange"
              style="width: 600px"
            >
              <template v-if="goodsSearchLoading" slot="notFoundContent">
                <a-spin size="small" />
              </template>
              <template v-else slot="notFoundContent">
                <span>暂无数据</span>
              </template>
              <a-select-option v-for="item in goodsOptions" :key="item.id" :value="item.id">
                {{ item.goodsName }} <span v-if="item.goodsCode" style="color: #999">({{ item.goodsCode }})</span>
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-form>

        <!-- 商品表格 -->
        <a-table
          :columns="goodsColumns"
          :data-source="selectedGoods"
          :pagination="false"
          size="small"
          rowKey="id"
          :scroll="{ y: 400 }"
        >
          <span slot="goodsName" slot-scope="text, record">
            <img v-if="record.goodsImage" :src="record.goodsImage" style="width: 40px; height: 40px; margin-right: 8px; vertical-align: middle;" />
            {{ record.goodsName }}
            <a-tag v-if="record.skuName" color="orange" style="margin-left: 8px">{{ record.skuName }}</a-tag>
            <div style="font-size: 12px; color: #999; margin-top: 4px;">
              当前可用库存: {{ record.stock || 0 }}
            </div>
          </span>
          <span slot="returnStock" slot-scope="text, record">
            <a-input-number
              v-model="record.returnStock"
              :min="1"
              :max="record.stock || 999999"
              :precision="0"
              style="width: 100px"
              placeholder="退货数量"
            />
          </span>
          <span slot="action" slot-scope="text, record">
            <a-button type="link" size="small" @click="removeGoods(record)">
              <a-icon type="delete" />移除
            </a-button>
          </span>
        </a-table>
      </div>
    </a-spin>
  </a-modal>
</template>

<script>
import { batchReturnInventory, getPlatformPurchaseInventoryList } from '@/api/retail/inventory'
import { getAllSuppliers } from '@/api/retail/supplier'
import { debounce } from 'lodash'

export default {
  name: 'ReturnForm',
  data() {
    return {
      supplierOptions: [],

      visible: false,
      confirmLoading: false,
      title: '采购退货',
      selectedGoodsIds: [],
      selectedGoods: [],
      goodsOptions: [],
      goodsSearchLoading: false,
      notFoundContent: '暂无数据',
      form: {
        orgId: undefined,
        orgType: undefined,
        supplierId: undefined,
        returnReason: undefined,
        remark: ''
      },
      rules: {
        supplierId: [{ required: true, message: '请选择退货供应商', trigger: 'change' }],
        returnReason: [{ required: true, message: '请选择退货原因', trigger: 'change' }]
      },
      goodsColumns: [
        {
          title: '商品信息',
          dataIndex: 'goodsName',
          key: 'goodsName',
          scopedSlots: { customRender: 'goodsName' },
          width: 300,
          fixed: 'left'
        },
        {
          title: '退货数量',
          dataIndex: 'returnStock',
          key: 'returnStock',
          scopedSlots: { customRender: 'returnStock' },
          width: 110,
          align: 'center'
        },
        {
          title: '操作',
          key: 'action',
          scopedSlots: { customRender: 'action' },
          width: 80,
          align: 'center',
          fixed: 'right'
        }
      ]
    }
  },
  created() {
    this.handleSearch = debounce(this.searchGoods, 300)
  },
  methods: {
    handleSearch(value) {
      console.log('ReturnForm handleSearch:', value)
      if (!value || value.trim() === '') {
        // 搜索框为空时，不重新加载
        return
      }
      // 有搜索内容时，执行搜索 - 使用平台/商家已有库存商品
      this.goodsSearchLoading = true
      getPlatformPurchaseInventoryList({
        goodsName: value,
        current: 1,
        size: 50
      }).then(response => {
        this.goodsOptions = response.data?.records || []
        this.goodsSearchLoading = false
      }).catch(() => {
        this.goodsSearchLoading = false
      })
    },
    handleDropdownVisibleChange(visible) {
      // 当下拉框显示时，如果没有数据，加载全部商品
      if (visible && this.goodsOptions.length === 0 && !this.goodsSearchLoading) {
        this.loadAllGoods()
      }
    },
    handleAdd() {
      this.form.orgId = this.$store.getters.orgId
      this.form.orgType = this.$store.getters.orgType
      this.form.supplierId = undefined
      this.form.returnReason = undefined
      this.form.remark = ''
      this.selectedGoodsIds = []
      this.selectedGoods = []
      this.goodsOptions = []
      this.supplierOptions = []
      // 加载初始商品列表和供应商列表
      this.loadAllGoods()
      this.getSupplierList()
      this.visible = true
    },
    getSupplierList() {
      getAllSuppliers().then(response => {
        this.supplierOptions = response.data || []
      }).catch(() => {
        this.supplierOptions = []
      })
    },
    loadAllGoods() {
      this.goodsSearchLoading = true
      getPlatformPurchaseInventoryList({
        current: 1,
        size: 50
      }).then(response => {
        this.goodsOptions = response.data?.records || []
        this.goodsSearchLoading = false
      }).catch(() => {
        this.goodsSearchLoading = false
      })
    },
    handleGoodsChange(value) {
      console.log('ReturnForm handleGoodsChange:', value)

      const existingIds = this.selectedGoods.map(item => item.id)
      const newIds = value.filter(id => !existingIds.includes(id))

      if (newIds.length > 0) {
        // 直接使用 goodsOptions 中的商品信息（已包含库存信息）
        for (const id of newIds) {
          const goods = this.goodsOptions.find(item => item.id === id)
          if (goods) {
            this.selectedGoods.push({
              ...goods,
              returnStock: undefined,
              // 使用库存字段
              availableStock: goods.stock || 0
            })
          }
        }
      }

      const filteredGoods = this.selectedGoods.filter(item => {
        return value.includes(item.id)
      })
      this.selectedGoods = filteredGoods
    },
    removeGoods(record) {
      // 从多选框中移除
      const index = this.selectedGoodsIds.indexOf(record.id)
      if (index > -1) {
        this.selectedGoodsIds.splice(index, 1)
      }
      // 从已选列表中移除
      const idx = this.selectedGoods.findIndex(item => item.id === record.id)
      if (idx > -1) {
        this.selectedGoods.splice(idx, 1)
      }
    },
    handleSubmit() {
      this.$refs.form.validate(valid => {
        if (!valid) {
          return false
        }

        if (this.selectedGoods.length === 0) {
          this.$message.warning('请选择退货商品')
          return
        }

        // 验证数据完整性
        const invalidGoods = this.selectedGoods.find(item => {
          return !item.returnStock || item.returnStock <= 0
        })

        if (invalidGoods) {
          this.$message.warning('请设置所有商品的退货数量')
          return
        }

        this.confirmLoading = true

        // 批量退货
        const returnList = this.selectedGoods.map(item => ({
          goodsId: item.goodsId,
          skuId: item.skuId,
          stock: item.returnStock,
          supplierId: this.form.supplierId,
          returnReason: this.form.returnReason,
          remark: this.form.remark
        }))

        // 保存采购退货
        batchReturnInventory(returnList).then(() => {
          this.$message.success('采购退货成功')
          this.visible = false
          this.$emit('ok')
        }).catch(error => {
          this.$message.error('采购退货失败：' + (error.message || '未知错误'))
        }).finally(() => {
          this.confirmLoading = false
        })
      })
    },
    handleCancel() {
      this.visible = false
      this.selectedGoodsIds = []
      this.selectedGoods = []
      this.form.supplierId = undefined
      this.form.returnReason = undefined
      this.form.remark = ''
    }
  }
}
</script>

<style scoped>
:deep(.ant-table) {
  font-size: 13px;
}
</style>
